import { createAction } from "@reduxjs/toolkit";
import type {
  SaveAction,
  SaveFormResult,
} from "../../../components/Posts/PostForm/types";

import type {
  LoadedDraft,
  Post,
  PostFormState,
  PostMediaFile,
} from "../../../shared/types/post";
import type {
  ActionOnCloseSelectedType,
  FormSaveData,
  OpenBulkCreatePostModal,
  PostCloseBehavior,
  PostUpdateSaveBehavior,
} from "./bulk-create-post.types";
import { PostStatesType } from "../../../constants/PostsView";
import { openModal } from "../../../libs/modals";
import { AccountType } from "../../../shared/types/accounts";
import { uniq } from "lodash";
import { extractCorePost } from "../../../utils/posts";
import { START_POST_FORM_ROUTINE } from "../../../constants/ActionTypes";
import { StartPostFormSuccessPayload } from "../../../reducers/types";
import { MediaSelectModalProps } from "./media-select.modal";
import type { TrackEventParams } from "../../../libs/analytics";

export const BULK_CREATE_POST_MODAL = "BULK_CREATE_POST_MODAL";
export const BULK_CREATE_POST_MEDIA_SELECT_MODAL =
  "BULK_CREATE_POST_MEDIA_SELECT_MODAL";

export const openBulkCreatePostModal = createAction<OpenBulkCreatePostModal>(
  `OPEN_${BULK_CREATE_POST_MODAL}`
);
export const closeBulkCreatePostModal = createAction(
  `CLOSE_${BULK_CREATE_POST_MODAL}`
);

export const addPostForm = createAction<OpenBulkCreatePostModal>(
  `ADD_POST_FORM_${BULK_CREATE_POST_MODAL}`
);
export const setSaveData = createAction<{ id: string; data: FormSaveData }>(
  `SET_SAVE_DATA_${BULK_CREATE_POST_MODAL}`
);

export const setIsFormDirty = createAction<{ id: string; isDirty: boolean }>(
  `SET_IS_FORM_DIRTY_${BULK_CREATE_POST_MODAL}`
);
export const setCoverPreviewUrl = createAction<{
  id: string;
  url: string | null;
}>(`SET_COVER_PREVIEW_URL_${BULK_CREATE_POST_MODAL}`);

interface OpenCreatePostModalV2Params {
  accountIds?: string[];
  accounts?: (AccountType | undefined | null)[];
  post?: Partial<Post>;
  media?: MediaRequest;
  postCloseBehavior?: PostCloseBehavior;
}

interface MediaSelectModal {
  type: "media-select-modal";
}

interface SkipMedia {
  type: "skip-media";
}

interface ProvidedMedia {
  type: "provided-media";
  assets: PostMediaFile[];
}

type MediaRequest = MediaSelectModal | ProvidedMedia | SkipMedia;

export const openCreatePostModalV2 = ({
  accountIds: rawAccIds,
  accounts,
  media,
  post,
  postCloseBehavior,
}: OpenCreatePostModalV2Params) => {
  const accountIds = uniq([
    ...(accounts || []).filter((c) => !!c).map((acc) => acc._id),
    ...(rawAccIds || []),
  ]);

  const constructedPost: Partial<Post> = {
    ...post,
    accountIds,
  };

  switch (media?.type) {
    case "provided-media": {
      return openBulkCreatePostModal({
        requests: [
          {
            type: "create",
            post: constructedPost,
            assets: media.assets,
          },
        ],
        postCloseBehavior,
      });
    }
    case "skip-media": {
      return openBulkCreatePostModal({
        requests: [
          {
            type: "create",
            post: constructedPost,
          },
        ],
        postCloseBehavior,
      });
    }
  }

  return openModal({
    type: BULK_CREATE_POST_MEDIA_SELECT_MODAL,
    props: {
      post: constructedPost,
      postCloseBehavior,
    } satisfies MediaSelectModalProps,
  });
};

export const openEditPostModalV2 = ({
  post,
  postCloseBehavior,
  accountDisabled,
  saveBehavior,
  modalTitle,
}: {
  post: Post | LoadedDraft;
  postCloseBehavior?: PostCloseBehavior;
  accountDisabled?: boolean;
  saveBehavior?: PostUpdateSaveBehavior;
  modalTitle?: string;
}) => {
  return openBulkCreatePostModal({
    requests: [
      {
        type: "update",
        post: {
          ...extractCorePost(post),
          postState:
            undefined /** remove as it's wrong from BE, and will be detected */,
        },
        accountDisabled,
        saveBehavior,
        formTitle: modalTitle,
      },
    ],
    postCloseBehavior,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).openCreatePostModalV2 =
  openCreatePostModalV2; /** used in planner and angular modalService.js */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).openEditPostModalV2 =
  openEditPostModalV2; /** used in planner and angular modalService.js */

export interface ClosePostFormInBulkCreatePayload {
  postId: string;
  actionOnCloseSelected: ActionOnCloseSelectedType;
}

export const closePostFormInBulkCreate =
  createAction<ClosePostFormInBulkCreatePayload>(
    `CLOSE_POST_FORM_IN_${BULK_CREATE_POST_MODAL}`
  );

export interface SavePostFormPayload {
  form: SaveFormResult;
  postSaveAction: SaveAction;
  requestType: PostFormState["requestType"];
}

export const savePostFormInBulkCreate = createAction<SavePostFormPayload>(
  `SAVE_POST_FORM_${BULK_CREATE_POST_MODAL}`
);

export interface RequestPostsFromIdsPayload {
  ids: string[];
  state: PostStatesType;
}

export const requestPostsFromIds = createAction<RequestPostsFromIdsPayload>(
  `REQUEST_POSTS_FROM_IDS_${BULK_CREATE_POST_MODAL}`
);

export interface RequestPostsFromClusterId {
  requestedPost: Post;
  postCloseBehavior?: PostCloseBehavior;
  trackEvent: (params: TrackEventParams) => void;
}

export const requestPostsFromClusterId =
  createAction<RequestPostsFromClusterId>(
    `REQUEST_POSTS_FROM_CLUSTER_ID_${BULK_CREATE_POST_MODAL}`
  );

export interface DuplicatePostFormPayload {
  fromId: string;
}

export const duplicatePostFormInBulkCreate =
  createAction<DuplicatePostFormPayload>(
    `DUPLICATE_POST_FORM_${BULK_CREATE_POST_MODAL}`
  );

export const bulkStartPostForm = createAction(
  START_POST_FORM_ROUTINE.TRIGGER,
  function prepare(payload: StartPostFormSuccessPayload) {
    return { payload };
  }
);
